import classnames from "classnames";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import * as Yup from "yup";
import { IRole } from "api/types/_role";
import { useRole } from "components/Hooks/UserHooks";
import { ROLES_FOR_APP, isHavePermissionRole } from "helpers/role";
import progileBg from "../../../assets/images/404-error.png";
import TooltipCustom from "components/Common/TooltipCustom";
import { LIST_OPERATOR_ROLE_OPTION } from "helpers/constans";
import { postUserAdmin, putUserAdmin } from "store/admin/thunk";
import NO_IMAGE from "../../../assets/images/no-image-icon.png";

export interface Props {
  isModal?: boolean;
  item?: any;
  isCopy?: boolean;
  triggerRefresh?: () => void;
  listRole?: IRole[];
}

registerLocale("en", en);
registerLocale("ko", ko);

const UserForm = ({ item , triggerRefresh }: Props) => {
  const { t } = useTranslation();
  const { userPermissions } = useRole();

  const LIST_OPERATOR_ROLE_OPTION_LANG = LIST_OPERATOR_ROLE_OPTION.map((i) => ({
    value: i.value,
    label: t(i?.label),
  }));

  const [passwordShow, setPasswordShow] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState("1");
  const [selectedCompany, setSelectedCompany] = useState<any>(null);

  const tabChange = (tab: any) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const handleSubmit = async (values: any) => {
    
    try {
      setIsLoading((_prev) => true);
      const data = {
        role_code: values?.role?.value ?? LIST_OPERATOR_ROLE_OPTION_LANG[0]?.value,
        name: values?.name ?? "",
        email: values?.email ?? "",
        is_active: values?.status === 1 ? true : false,
        company_id: values?.role?.value === "COMPANY" ? Number.parseInt(values?.company_id) : null,
        ...(item?.id ? {} : { password: values?.password ?? "" })
      };

      const response: any = item?.id ? await putUserAdmin(item?.id, data) : await postUserAdmin(data);
      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${item?.id ? t("The operator has been updated.") : t("The operator has been created.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(`${t("Name is required")}`),
    email: Yup.string()
      .email(`${t("Please enter a valid email address")}`)
      .required(`${t("Email is required")}`),
    password: !!item?.id
      ? Yup.string().nullable()
      : Yup.string()
          .min(6, `${t("Password must be at least 6 characters")}`)
          .required(`${t("Password is required")}`),
    status: Yup.boolean(),
    role: Yup.object()
      .shape({
        label: Yup.string(),
        value: Yup.string(),
      })
      .nullable(), //.required(`${t("Role is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      status: 1,
      role: undefined,
      company_id: null,
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (valueDefault: any) => {
    const vName = valueDefault?.name || "";
    const vEmail = valueDefault?.username || "";
    const vPassword = valueDefault?.password || "";
    const vIsActive = valueDefault?.isActive;
    const vRole = LIST_OPERATOR_ROLE_OPTION_LANG?.filter(
        (item: any) => String(valueDefault?.role?.code) === String(item?.value)
      )[0] || LIST_OPERATOR_ROLE_OPTION_LANG[0];

    const vCompany = valueDefault?.company && {
      value: valueDefault?.company?.id,
      label: (
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              height: "22px",
              width: "22px",
              overflow: "hidden",
              borderRadius: "15px",
              objectFit: "cover",
              backgroundColor: "#eaeaea",
              zIndex: 99,
            }}
          >
            <img
              src={valueDefault?.company?.logo_url || NO_IMAGE}
              alt={valueDefault?.company?.name}
              height={22}
              width={22}
              style={{
                minWidth: "22px",
                overflow: "hidden",
                borderRadius: "50%",
                objectFit: "cover",
                backgroundColor: "#eaeaea",
              }}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = NO_IMAGE;
              }}
            />
          </div>
          {valueDefault?.company?.name}
        </div>
      ),
      data: valueDefault?.company,
    };

    setTimeout(() => {
      formik.setFieldValue("name", vName);
      formik.setFieldValue("email", vEmail);
      formik.setFieldValue("password", vPassword);
      formik.setFieldValue("status", vIsActive);
      formik.setFieldValue("role", vRole);
      formik.setFieldValue("company_id", valueDefault?.company?.id);
      setSelectedCompany(vCompany);
    }, 300);
  };

  const handleSubmitChangePassword = async (values: any) => {
    try {
      setIsLoading((_prev) => true);
      const data = {
        password: values?.new_password ?? "",
        // status: initialValuesDefault?.status ?? 1,
      };
      const response: any = await putUserAdmin(item?.id, data);
      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("Your password has been changed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchemaChangePassword = Yup.object({
    new_password: Yup.string()
      .min(6, `${t("Password must be at least 6 characters")}`)
      .required(`${t("New Password is required")}`),
    confirm_password: Yup.string().oneOf(
      [Yup.ref("new_password")],
      `${t("Passwords must match")}`
    ),
  });

  const formikChangePassword = useFormik({
    initialValues: {
      new_password: "",
      confirm_password: "",
    },
    validationSchema: validationSchemaChangePassword,
    onSubmit: handleSubmitChangePassword,
  });

  const handleGeneratePassword = () => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()";
    let password: string = "";

    for (let i = 0; i < 10; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      password += characters[randomIndex];
    }
    formik.setFieldValue("password", password);
  };

  useEffect(() => {
    if(item) {
      handleSetValueForm(item)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  return (
    <React.Fragment>
      <div>
        <Container>
          <div className="position-relative mx-n4 mt-n4">
            <div
              className="profile-wid-bg profile-setting-img"
              style={{ height: "100px" }}
            >
              <img src={progileBg} className="profile-wid-img" alt="" />
            </div>
          </div>
          <Row>
            <Col sm={12} className="mt-3">
              <Card className="mt-xxl-n5">
                <CardHeader>
                  <Nav
                    className="nav-tabs-custom rounded card-header-tabs border-bottom-0"
                    role="tablist"
                  >
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "1" })}
                        onClick={() => {
                          tabChange("1");
                        }}
                      >
                        {t("Personal Details")}
                      </NavLink>
                    </NavItem>
                    {item?.id &&
                      isHavePermissionRole(
                        ROLES_FOR_APP.OPERATOR_UPDATE,
                        userPermissions
                      ) && (
                        <NavItem>
                          <NavLink
                            to="#"
                            className={classnames({
                              active: activeTab === "2",
                            })}
                            onClick={() => {
                              tabChange("2");
                            }}
                            type="button"
                          >
                            {t("Change Password")}
                          </NavLink>
                        </NavItem>
                      )}
                  </Nav>
                </CardHeader>
                <CardBody className="p-4">
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      <form onSubmit={formik.handleSubmit}>
                        <Row>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor="firstnameInput"
                                className="form-label"
                              >
                                {t("Name")}{" "}
                                <span className="text-danger">*</span>
                              </Label>
                              <Input
                                name="name"
                                type="text"
                                className="form-control"
                                id="firstnameInput"
                                placeholder={t("Enter your name")}
                                value={formik?.values?.name}
                                onChange={(event: any) =>
                                  formik.setFieldValue(
                                    "name",
                                    event?.target?.value
                                  )
                                }
                              />
                              {formik.touched.name && formik.errors.name ? (
                                <div className="text-danger mt-2">
                                  {formik.errors.name}
                                </div>
                              ) : null}
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor="emailInput"
                                className="form-label"
                              >
                                {t("Email Address")}{" "}
                                <span className="text-danger"> *</span>
                              </Label>
                              <Input
                                name="email"
                                type="text"
                                className="form-control"
                                id="emailInput"
                                placeholder={t("Enter your email")}
                                value={formik?.values?.email}
                                onChange={(event: any) =>
                                  formik.setFieldValue(
                                    "email",
                                    event?.target?.value
                                  )
                                }
                              />
                              {formik.touched.email && formik.errors.email ? (
                                <div className="text-danger mt-2">
                                  {formik.errors.email}
                                </div>
                              ) : null}
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor="lastnameInput"
                                className="form-label"
                              >
                                {t("Role")}{" "}
                                <span className="text-danger"> *</span>
                              </Label>
                              <Select
                                name="role"
                                className="mb-0 dropdown-status-rounded"
                                classNamePrefix="name-prefix"
                                placeholder={t("Role")}
                                value={
                                  formik?.values?.role || LIST_OPERATOR_ROLE_OPTION_LANG[0]
                                }
                                onChange={(event: any) =>
                                  formik.setFieldValue("role", event)
                                }
                                options={LIST_OPERATOR_ROLE_OPTION_LANG}
                              ></Select>
                              {formik.touched.role && formik.errors.role ? (
                                <div className="text-danger mt-2">
                                  {formik.errors.role}
                                </div>
                              ) : null}
                            </div>
                          </Col>

                          {!item?.id && (
                            <Col lg={6}>
                              <div className="mb-3">
                                <Label
                                  htmlFor="passwordInput"
                                  className="form-label"
                                >
                                  {t("Password")}{" "}
                                  <span className="text-danger"> *</span>
                                </Label>
                                <div className="position-relative">
                                  <Input
                                    name="password"
                                    type={passwordShow ? "text" : "password"}
                                    className="form-control"
                                    id="passwordInput"
                                    autoComplete="off"
                                    placeholder={t("Enter Password")}
                                    value={formik?.values?.password}
                                    onChange={(event: any) =>
                                      formik.setFieldValue(
                                        "password",
                                        event?.target?.value
                                      )
                                    }
                                  />
                                  <div
                                    className="d-flex position-absolute end-0 top-0"
                                    style={{ listStyle: "none" }}
                                  >
                                    <TooltipCustom
                                      title={
                                        !!passwordShow
                                          ? t("Hidden Password")
                                          : t("Show Password")
                                      }
                                    >
                                      <button
                                        className="btn btn-link text-decoration-none text-muted"
                                        onClick={() =>
                                          setPasswordShow((prev) => !prev)
                                        }
                                        type="button"
                                        id="password-addon"
                                      >
                                        {!!passwordShow ? (
                                          <i className="ri ri-eye-off-line align-middle"></i>
                                        ) : (
                                          <i className="ri ri-eye-line align-middle"></i>
                                        )}
                                      </button>
                                    </TooltipCustom>
                                    <TooltipCustom
                                      title={t("Generate Password")}
                                    >
                                      <button
                                        className="btn btn-link text-decoration-none text-muted"
                                        onClick={() => handleGeneratePassword()}
                                        type="button"
                                      >
                                        <i className="ri ri-refresh-line align-middle"></i>
                                      </button>
                                    </TooltipCustom>
                                  </div>
                                </div>
                                {formik.touched.password &&
                                formik.errors.password ? (
                                  <div className="text-danger mt-2">
                                    {formik.errors.password}
                                  </div>
                                ) : null}
                              </div>
                            </Col>
                          )}

                          <Col lg={12}>
                            <div className="mb-3">
                              <ul className="list-unstyled mb-0">
                                <li className="d-flex">
                                  <div className="flex-grow-1">
                                    <label
                                      htmlFor="directMessage"
                                      className="form-check-label fs-14"
                                    >
                                      {t("Setting Active")}
                                    </label>
                                    <p className="text-muted">
                                      {t(
                                        "Allows the account to log into the system"
                                      )}
                                    </p>
                                  </div>
                                  <div className="flex-shrink-0">
                                    <div className="form-check form-switch">
                                      <Input
                                        name="status"
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="directMessage"
                                        checked={
                                          !!Number(formik?.values?.status)
                                        }
                                        onChange={(event: any) =>
                                          formik.setFieldValue(
                                            "status",
                                            Number(event?.target.checked)
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                </li>
                              </ul>
                            </div>
                          </Col>

                          <Col lg={12}>
                            <div className="hstack gap-2 justify-content-end">
                              {isHavePermissionRole(
                                ROLES_FOR_APP.OPERATOR_UPDATE,
                                userPermissions
                              ) && (
                                <button
                                  type="submit"
                                  className="btn btn-primary fs-14"
                                >
                                  {isLoading ? (
                                    <Spinner
                                      size="sm"
                                      className="me-2"
                                    ></Spinner>
                                  ) : !!item?.id ? (
                                    <i className="ri-login-circle-line align-bottom me-2 "></i>
                                  ) : (
                                    <i className="ri-add-fill align-bottom me-2"></i>
                                  )}
                                  {item?.id ? t("Submit Update") : t("Submit Button Create")}
                                </button>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </form>
                    </TabPane>
                    <TabPane tabId="2">
                      <form onSubmit={formikChangePassword.handleSubmit}>
                        <Row className="g-2">
                          <Col lg={6}>
                            <div>
                              <Label
                                htmlFor="newpasswordInput"
                                className="form-label"
                              >
                                {t("New Password")}{" "}
                                <span className="text-danger"> *</span>
                              </Label>
                              <Input
                                name="new_password"
                                type="password"
                                className="form-control"
                                id="newpasswordInput"
                                placeholder={t("New Password")}
                                value={
                                  formikChangePassword?.values?.new_password
                                }
                                onChange={(event: any) =>
                                  formikChangePassword.setFieldValue(
                                    "new_password",
                                    event?.target?.value
                                  )
                                }
                              />
                              {formikChangePassword.touched.new_password &&
                              formikChangePassword.errors.new_password ? (
                                <div className="text-danger mt-2">
                                  {formikChangePassword.errors.new_password}
                                </div>
                              ) : null}
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div>
                              <Label
                                htmlFor="confirmpasswordInput"
                                className="form-label"
                              >
                                {t("Confirm Password")}{" "}
                                <span className="text-danger"> *</span>
                              </Label>
                              <Input
                                name="confirm_password"
                                type="password"
                                className="form-control"
                                id="confirmpasswordInput"
                                placeholder={t("Confirm Password")}
                                value={
                                  formikChangePassword?.values?.confirm_password
                                }
                                onChange={(event: any) =>
                                  formikChangePassword.setFieldValue(
                                    "confirm_password",
                                    event?.target?.value
                                  )
                                }
                              />
                              {formikChangePassword.touched.confirm_password &&
                              formikChangePassword.errors.confirm_password ? (
                                <div className="text-danger mt-2">
                                  {formikChangePassword.errors.confirm_password}
                                </div>
                              ) : null}
                            </div>
                          </Col>
                          <Col lg={12}>
                            <div className="text-end mt-3">
                              {isHavePermissionRole(
                                ROLES_FOR_APP.OPERATOR_UPDATE,
                                userPermissions
                              ) && (
                                <button
                                  style={{ width: "180px" }}
                                  type="submit"
                                  className="btn btn-primary fs-14"
                                >
                                  {isLoading && (
                                    <Spinner size="sm me-2"></Spinner>
                                  )}
                                  {t("Change Password")}
                                </button>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </form>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer closeButton={false} limit={1} />
    </React.Fragment>
  );
};

export default UserForm;
